// PageHeader.tsx - Restored reusable page header with contextual breadcrumb
import { Button } from "primereact/button";
import React from "react";
import { useNavigate } from "react-router-dom";

interface PageHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBack?: () => void;
  beforeTitle?: React.ReactNode;
  extra?: React.ReactNode;
  hideBreadcrumb?: boolean;
  backTo?: string;
  backState?: any;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ComponentType<any>;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  showBackButton = false,
  beforeTitle,
  extra,
  backTo,
  backState,
  onBack,
}) => {
  const navigate = useNavigate();

  // Set the page title in the breadcrumb area
  React.useEffect(() => {
    const pageTitleElement = document.getElementById("page-title");
    const beforeTitleElement = document.getElementById("before-page-title");

    if (pageTitleElement) {
      pageTitleElement.textContent = title;
    }

    // Inject beforeTitle content if provided
    if (beforeTitleElement && beforeTitle) {
      const container = document.createElement("span");
      container.className = "d-inline-flex align-items-center me-2";
      beforeTitleElement.innerHTML = "";
      beforeTitleElement.appendChild(container);

      // Use React to render the beforeTitle content
      import("react-dom/client").then(({ createRoot }) => {
        const root = createRoot(container);
        root.render(beforeTitle as React.ReactElement);
      });
    }

    return () => {
      if (pageTitleElement) {
        pageTitleElement.textContent = "";
      }
      if (beforeTitleElement) {
        beforeTitleElement.innerHTML = "";
      }
    };
  }, [title, beforeTitle]);

  // If only using beforeTitle without showBackButton or extra, don't render the header div
  if (!showBackButton && !extra) {
    return null;
  }

  return (
    <div className="content-header py-2">
      <div className="container">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {showBackButton && (
              <Button
                icon="pi pi-arrow-left"
                rounded
                text
                aria-label="Go Back"
                onClick={() => {
                  if (onBack) onBack();
                  else if (backTo) navigate(backTo);
                  else navigate(-1);
                }}
                className="p-button-text p-0"
              />
            )}
          </div>
          {extra && (
            <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
              {extra}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
